import ClearIcon from "@mui/icons-material/Clear";
import { Stack, Typography } from "@mui/material";
import { useIsMounted, type RefetchMode } from "@shared/frontend/hooks";
import { useHyperTable } from "@shared/frontend/hyper-table/HyperTable";
import React, { useCallback } from "react";
import { createPortal } from "react-dom";
import { repo, type EntityFilter } from "remult";
import type { HyperTableContextValue } from "../../types";
import "./ActionsBar.scss";

/**
 * Description:
 * ---
 * Represents the result of an action button click.
 * Allows specifying whether to refetch data or reset the selection.
 *
 * ---
 *
 * Properties:
 * ---
 * - `refetch`: Determines if the table data should be refetched. Can be a boolean or a specific `RefetchMode`.
 * - `resetSelection`: Indicates whether the row selection should be cleared.
 *
 * ---
 * Recommendations:
 * ---
 * - Use this to control the behavior of the table after an action is performed.
 */
export interface ActionButtonAfter {
  /**
   * Determines if the table data should be refetched.
   * Can be a boolean ("remove") or a specific `RefetchMode`.
   */
  refetch?: RefetchMode | boolean;

  /**
   * Indicates whether the row selection should be cleared.
   */
  resetSelection?: boolean;
}

/**
 * Description:
 * ---
 * Defines the structure of an action button in the `ActionsBar`.
 * Each button performs a specific action when clicked.
 *
 * ---
 *
 * Properties:
 * ---
 * - `onClick`: A callback function triggered when the button is clicked. Provides event details, filter criteria, and table context.
 * - `title`: The label displayed on the button.
 * - `icon`: The icon displayed alongside the button title.
 *
 * ---
 * Recommendations:
 * ---
 * - Use this to define custom actions for selected rows in the table.
 * - Ensure the `onClick` handler handles both individual and bulk row selections.
 */
export interface ActionButton<Entity> {
  /**
   * A callback function triggered when the button is clicked.
   * Provides event details, filter criteria, and table context.
   */
  onClick: (e: {
    event: React.MouseEvent<HTMLButtonElement>;
    whereToUpdate: EntityFilter<Entity>;
    context: HyperTableContextValue<Entity>;
  }) =>
    | Promise<ActionButtonAfter | undefined>
    | ActionButtonAfter
    | undefined
    | void
    | Promise<void>;

  /**
   * The label displayed on the button.
   */
  title: string;

  /**
   * The icon displayed alongside the button title.
   */
  icon: React.ReactNode;
}

/**
 * Description:
 * ---
 * A function type for dynamically generating action buttons based on the table's state.
 *
 * ---
 *
 * Parameters:
 * ---
 * - `selectedCount`: The number of rows currently selected.
 * - `context`: The context of the `HyperTable`, providing access to its state and actions.
 *
 * ---
 * Returns:
 * ---
 * - An array of `ActionButton` objects.
 *
 * ---
 * Recommendations:
 * ---
 * - Use this to create dynamic action buttons based on the number of selected rows or other table states.
 */
export type ActionButtonsGetter<Entity> = (params: {
  /**
   * The number of rows currently selected.
   */
  selectedCount: number;

  /**
   * The context of the `HyperTable`, providing access to its state and actions.
   */
  context: HyperTableContextValue<Entity>;
}) => ActionButton<Entity>[];

/**
 * Description:
 * ---
 * Props for the `ActionsBar` component, which provides bulk actions for selected rows.
 *
 * ---
 *
 * Properties:
 * ---
 * - `buttons`: An array of `ActionButton` objects or a function to dynamically generate them.
 *
 * ---
 * Recommendations:
 * ---
 * - Use this to configure the actions available in the `ActionsBar`.
 * - Pass either a static array of buttons or a dynamic function to generate them.
 */
interface ActionsBarProps<Entity> {
  /**
   * An array of `ActionButton` objects or a function to dynamically generate them.
   */
  buttons: ActionButton<Entity>[] | ActionButtonsGetter<Entity>;

  onClose?: () => void;
}

export const ActionsBar = <Entity extends any>(
  props: ActionsBarProps<Entity>,
) => {
  const { buttons, onClose } = props;
  const ctx = useHyperTable<Entity>();
  const {
    table,
    setAllSelected,
    allSelected,
    getEntity,
    finalWhere,
    selectedIds,
  } = ctx;

  const isMounted = useIsMounted();

  const itemsCount = allSelected ? table.getRowCount() : selectedIds.length;

  const handleClose = useCallback(() => {
    table.setRowSelection({});
    setAllSelected(false);
    onClose?.();
  }, [table, onClose]);

  const handleButtonClick = useCallback(
    async (
      e: React.MouseEvent<HTMLButtonElement>,
      button: ActionButton<Entity>,
    ) => {
      const eRepo = repo(getEntity());
      const res = await button.onClick({
        event: e,
        whereToUpdate: allSelected
          ? (finalWhere as EntityFilter<Entity>)
          : eRepo.metadata.idMetadata.getIdFilter(...selectedIds),
        context: ctx,
      });

      const { refetch = false, resetSelection = false } = res ?? {};

      if (isMounted.current) {
        if (resetSelection) {
          ctx.setAllSelected(false);
          ctx.table.setRowSelection({});
        }
        if (refetch) {
          ctx.refetch(typeof refetch === "boolean" ? "remove" : refetch);
        }
      }
    },
    [ctx, isMounted],
  );

  if (itemsCount === 0) return null;

  const btnsArray =
    buttons instanceof Function
      ? buttons({ context: ctx, selectedCount: itemsCount })
      : buttons;

  return createPortal(
    <div className="actions-container">
      <Stack direction="row" gap="0.3rem">
        <p className="items-counter">{itemsCount}</p>
        <div className="line" />
      </Stack>
      <Stack direction="row" width="75%" justifyContent="center" gap="1vw">
        {btnsArray.map((button) => {
          return (
            <button
              className="action-button"
              key={button.title}
              onClick={(e) => {
                e.persist(); // Prevent React from clearing the event

                handleButtonClick(e, button);
              }}
            >
              <Typography
                component="span"
                sx={{ width: "1.5rem", height: "1.5rem" }}
              >
                {button.icon}
              </Typography>
              {button.title}
            </button>
          );
        })}
      </Stack>
      <Stack direction="row" gap="0.3rem">
        <div className="line" />
        <ClearIcon
          onClick={handleClose}
          sx={{ color: "white", fontSize: "2rem", cursor: "pointer" }}
        />
      </Stack>
    </div>,
    document.getElementById("root") as HTMLElement,
  );
};
